import { PlusOutlined, ThunderboltOutlined } from '@ant-design/icons';
import { App, Button, Form, Input, Modal, Popconfirm, Switch, Table, Tag, Tooltip } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import PageTitle from 'components/page-title';
import {
  createEvent,
  createFromWeb2026,
  getEventCounts,
  listEvents,
  setShowOnHome,
} from './api';
import { EventCounts, GalaEvent, STATUS_META, errorText, formatVn } from './contract';
import styles from './styles.module.scss';

const eventPath = (id: number) => `/gala/${id}`;

/** Danh sách sự kiện Gala (docs/gala/01-dac-ta-2026-10-13.md §8). */
const GalaListPage = () => {
  const { message } = App.useApp();
  const navigate = useNavigate();
  const [events, setEvents] = useState<GalaEvent[]>([]);
  const [counts, setCounts] = useState<Record<number, EventCounts | null>>({});
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState<number | null>(null);
  const [creatingFromWeb, setCreatingFromWeb] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [form] = Form.useForm<{ title: string }>();

  const load = useCallback(async () => {
    try {
      const rows = await listEvents();
      setEvents(Array.isArray(rows) ? rows : []);
      return rows;
    } catch (e) {
      message.error(errorText(e, 'Không tải được danh sách sự kiện Gala.'));
      return [];
    } finally {
      setLoading(false);
    }
  }, [message]);

  useEffect(() => {
    load();
  }, [load]);

  // Số vé phát / đã vào: server không kèm trong danh sách -> hỏi từng sự kiện sau khi bảng đã hiện.
  useEffect(() => {
    let active = true;
    events.forEach((event) => {
      getEventCounts(event.id).then((c) => {
        if (active) setCounts((prev) => ({ ...prev, [event.id]: c }));
      });
    });
    return () => {
      active = false;
    };
  }, [events]);

  const toggleHome = async (event: GalaEvent, on: boolean) => {
    setBusyId(event.id);
    try {
      await setShowOnHome(event.id, on);
      message.success(on ? 'Đã bật thẻ Gala ở Trang chủ.' : 'Đã tắt thẻ Gala ở Trang chủ.');
      await load();
    } catch (e) {
      message.error(errorText(e, 'Không đổi được công tắc Trang chủ.'));
    } finally {
      setBusyId(null);
    }
  };

  const onCreate = async () => {
    const { title } = await form.validateFields();
    setCreating(true);
    try {
      const event = await createEvent({ title: title.trim() });
      message.success('Đã tạo sự kiện.');
      setCreateOpen(false);
      form.resetFields();
      navigate(eventPath(event.id));
    } catch (e) {
      message.error(errorText(e, 'Không tạo được sự kiện.'));
    } finally {
      setCreating(false);
    }
  };

  const onCreateFromWeb = async () => {
    setCreatingFromWeb(true);
    try {
      const event = await createFromWeb2026();
      message.success('Đã tạo sự kiện HP GALA 2026 (bản nháp) kèm 10 hạng mục và 4 khối nội dung.');
      navigate(eventPath(event.id));
    } catch (e) {
      message.error(errorText(e, 'Không tạo được sự kiện mẫu.'));
    } finally {
      setCreatingFromWeb(false);
    }
  };

  const columns: ColumnsType<GalaEvent> = [
    {
      title: 'Tiêu đề',
      dataIndex: 'title',
      render: (_: string, row) => (
        <div className={styles.titleCell}>
          <Link to={eventPath(row.id)}>{row.title}</Link>
          {row.eyebrow && <span className={styles.subtle}>{row.eyebrow}</span>}
        </div>
      ),
    },
    {
      title: 'Ngày giờ (giờ Việt Nam)',
      dataIndex: 'eventAt',
      width: 190,
      render: (v: string | null) => <span className={styles.nowrap}>{formatVn(v)}</span>,
    },
    {
      title: 'Trạng thái',
      dataIndex: 'status',
      width: 130,
      render: (v: GalaEvent['status']) => (
        <Tag color={STATUS_META[v]?.color}>{STATUS_META[v]?.text ?? v}</Tag>
      ),
    },
    {
      title: (
        <Tooltip title="Vé đã phát / đã check-in">
          <span>Vé phát / đã vào</span>
        </Tooltip>
      ),
      width: 150,
      render: (_: unknown, row) => {
        if (!(row.id in counts)) return <span className={styles.subtle}>…</span>;
        const c = counts[row.id];
        return c ? (
          <span className={styles.nowrap}>
            {c.issued} / {c.checkedIn}
          </span>
        ) : (
          '—'
        );
      },
    },
    {
      title: 'Trang chủ',
      dataIndex: 'showOnHome',
      width: 110,
      render: (v: boolean, row) => (
        <Tooltip title="Bật thì thẻ Gala hiện ở Trang chủ app. Chỉ một sự kiện được bật.">
          <Switch
            checked={v}
            loading={busyId === row.id}
            aria-label={`Hiện thẻ Gala "${row.title}" ở Trang chủ`}
            onChange={(on) => toggleHome(row, on)}
          />
        </Tooltip>
      ),
    },
    {
      title: '',
      width: 90,
      render: (_: unknown, row) => <Link to={eventPath(row.id)}>Mở</Link>,
    },
  ];

  return (
    <div>
      <PageTitle title="GALA" />
      <div className={styles.toolbar}>
        <Button type="primary" icon={<PlusOutlined />} onClick={() => setCreateOpen(true)}>
          Tạo sự kiện
        </Button>
        <Popconfirm
          title="Tạo sự kiện mẫu HP GALA 2026?"
          description="Tạo bản nháp: ngày 24.01.2027 19:00, 10 hạng mục và 4 khối nội dung (Giới thiệu, Lộ trình 3 chặng, Tổng kết, Lưu ý)."
          okText="Tạo"
          cancelText="Không"
          onConfirm={onCreateFromWeb}
        >
          <Button icon={<ThunderboltOutlined />} loading={creatingFromWeb}>
            Tạo từ web GALA 2026
          </Button>
        </Popconfirm>
      </div>

      <Table<GalaEvent>
        rowKey="id"
        loading={loading}
        columns={columns}
        dataSource={events}
        pagination={false}
        scroll={{ x: 'max-content' }}
        locale={{ emptyText: 'Chưa có sự kiện Gala nào. Bấm "Tạo từ web GALA 2026" để có bản mẫu.' }}
      />

      <Modal
        title="Tạo sự kiện Gala"
        open={createOpen}
        okText="Tạo"
        cancelText="Huỷ"
        confirmLoading={creating}
        onOk={onCreate}
        onCancel={() => setCreateOpen(false)}
        destroyOnHidden
      >
        <Form form={form} layout="vertical" preserve={false}>
          <Form.Item
            name="title"
            label="Tiêu đề"
            rules={[
              { required: true, whitespace: true, message: 'Nhập tiêu đề sự kiện.' },
              { max: 200, message: 'Không quá 200 ký tự.' },
            ]}
          >
            <Input autoFocus placeholder="Ví dụ: HP GALA 2026" onPressEnter={onCreate} />
          </Form.Item>
          <p className={styles.hint}>Các thông tin khác điền ở trang sự kiện sau khi tạo.</p>
        </Form>
      </Modal>
    </div>
  );
};

export default GalaListPage;
